import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ClientPreset } from '../types/client';
import { clientsApi, Client } from '../lib/api';
import { useAuth } from './AuthContext';

interface ClientPresetContextType {
  presets: ClientPreset[];
  selectedPreset: ClientPreset | null;
  addPreset: (preset: Omit<ClientPreset, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updatePreset: (id: string, preset: Partial<ClientPreset>) => void;
  deletePreset: (id: string) => void;
  selectPreset: (id: string) => void;
  clearSelection: () => void;
}

const ClientPresetContext = createContext<ClientPresetContextType | undefined>(undefined);

// Default presets (now only used as fallback templates)
const defaultPresets: ClientPreset[] = [
  {
    id: 'marti-herb',
    name: 'Marti Herb',
    brand: 'Marti Herb',
    metaAccountId: 'act_540662072311011',
    pageId: '572093109311171',
    pageName: 'Marti Herb',
    pixelId: '1138225738072124',
    defaultBudget: 100,
    defaultDuration: 30,
    defaultCountries: ['US'],
    defaultAgeMin: 25,
    defaultAgeMax: 55,
    defaultGenders: [1, 2],
    primaryColor: 'var(--color-systemGreen)',
    secondaryColor: 'var(--color-systemGreen)',
    website: 'https://martimart.com',
    industry: 'Health & Wellness',
    utmTemplate: 'utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

// Helper function to convert database client to preset format
const clientToPreset = (client: Client): ClientPreset => ({
  id: `db-client-${client.id}`,
  name: client.name,
  brand: client.brand,
  metaAccountId: client.meta_account_id,
  pageId: client.page_id,
  pageName: client.page_name,
  pixelId: client.pixel_id,
  defaultBudget: client.default_budget / 100, // Convert from cents to dollars
  defaultDuration: client.default_duration,
  defaultCountries: client.default_countries,
  defaultAgeMin: client.default_age_min,
  defaultAgeMax: client.default_age_max,
  defaultGenders: client.default_genders,
  primaryColor: client.primary_color,
  secondaryColor: client.secondary_color,
  website: client.website || '',
  industry: client.industry || '',
  utmTemplate: client.utm_template || 'utm_source={{site_source_name}}&utm_medium={{placement}}&utm_term={{adset.name}}+{{ad.name}}&utm_content={{ad.id}}&utm_campaign={{campaign.name}}&fbadid={{ad.id}}&tw_source={{site_source_name}}&tw_adid={{ad.id}}',
  createdAt: client.created_at,
  updatedAt: client.created_at
});

export const ClientPresetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [presets, setPresets] = useState<ClientPreset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<ClientPreset | null>(null);
  const { user, loading: authLoading } = useAuth();

  // Load user's database clients when authenticated
  useEffect(() => {
    const loadUserClients = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        console.log('ClientPresetContext: Auth still loading, waiting...');
        return;
      }

      if (!user) {
        // No user logged in, clear everything
        console.log('ClientPresetContext: No user, clearing presets');
        setPresets([]);
        setSelectedPreset(null);
        return;
      }

      // Add a small delay to ensure token is properly set
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        console.log('ClientPresetContext: Fetching user clients for:', user.email);
        // Fetch user's database clients
        const response = await clientsApi.getClients();
        const dbClients = response.data.clients;
        
        console.log('ClientPresetContext: Got database clients:', dbClients);
        
        if (dbClients.length > 0) {
          // Convert database clients to presets
          const dbPresets = dbClients.map(clientToPreset);
          
          // Load localStorage presets and merge them
          const savedPresets = localStorage.getItem('clientPresets');
          const localPresets = savedPresets ? JSON.parse(savedPresets) : defaultPresets;
          
          // Combine database presets with local presets
          const allPresets = [...dbPresets, ...localPresets];
          setPresets(allPresets);
          
          // Auto-select the first database client (user's actual client)
          console.log('ClientPresetContext: Auto-selecting database client:', dbPresets[0].brand);
          setSelectedPreset(dbPresets[0]);
          localStorage.setItem('selectedPresetId', dbPresets[0].id);
        } else {
          // No database clients, fall back to local presets
          const savedPresets = localStorage.getItem('clientPresets');
          const localPresets = savedPresets ? JSON.parse(savedPresets) : defaultPresets;
          setPresets(localPresets);
          setSelectedPreset(null); // Don't auto-select if no real clients
        }
      } catch (error) {
        console.error('Error loading user clients:', error);
        // Fall back to local presets on error
    const savedPresets = localStorage.getItem('clientPresets');
        const localPresets = savedPresets ? JSON.parse(savedPresets) : defaultPresets;
        setPresets(localPresets);
        setSelectedPreset(null);
      }
    };

    loadUserClients();
  }, [user, authLoading]); // Include authLoading in dependencies

  // Load from localStorage on mount (but this will be overridden by database clients)
  useEffect(() => {
    const savedSelectedId = localStorage.getItem('selectedPresetId');
    
    if (savedSelectedId && presets.length > 0) {
      const selected = presets.find((p: ClientPreset) => p.id === savedSelectedId);
        if (selected) {
          setSelectedPreset(selected);
        }
      }
  }, [presets]);

  // Save to localStorage when presets change (only for local presets)
  useEffect(() => {
    // Only save local presets (not database clients) to localStorage
    const localPresets = presets.filter(p => !p.id.startsWith('db-client-'));
    localStorage.setItem('clientPresets', JSON.stringify(localPresets));
  }, [presets]);

  // Save selected preset to localStorage
  useEffect(() => {
    if (selectedPreset) {
      localStorage.setItem('selectedPresetId', selectedPreset.id);
    } else {
      localStorage.removeItem('selectedPresetId');
    }
  }, [selectedPreset]);

  const addPreset = (presetData: Omit<ClientPreset, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newPreset: ClientPreset = {
      ...presetData,
      id: `preset-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    setPresets(prev => [...prev, newPreset]);
  };

  const updatePreset = (id: string, updates: Partial<ClientPreset>) => {
    setPresets(prev => prev.map(preset => 
      preset.id === id 
        ? { ...preset, ...updates, updatedAt: new Date().toISOString() }
        : preset
    ));
    
    // Update selected preset if it's the one being updated
    if (selectedPreset?.id === id) {
      setSelectedPreset(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const deletePreset = (id: string) => {
    setPresets(prev => prev.filter(preset => preset.id !== id));
    
    // Clear selection if deleted preset was selected
    if (selectedPreset?.id === id) {
      setSelectedPreset(null);
    }
  };

  const selectPreset = (id: string) => {
    const preset = presets.find(p => p.id === id);
    setSelectedPreset(preset || null);
  };

  const clearSelection = () => {
    setSelectedPreset(null);
  };

  return (
    <ClientPresetContext.Provider value={{
      presets,
      selectedPreset,
      addPreset,
      updatePreset,
      deletePreset,
      selectPreset,
      clearSelection
    }}>
      {children}
    </ClientPresetContext.Provider>
  );
};

export const useClientPreset = () => {
  const context = useContext(ClientPresetContext);
  if (context === undefined) {
    throw new Error('useClientPreset must be used within a ClientPresetProvider');
  }
  return context;
}; 